import {
  css,
  Button,
  Container,
  Text,
  Card,
  tokens,
  timestampToDateString,
  timestampToTimeString,
} from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { storiesService } from "../lib/storiesClient";
import { useFullscreenIframe } from "../hooks/useFullscreenIframe";
import { toEmbeddableUrl, isYoutubeUrl } from "../lib/embedUrl";
import {
  Story as StoryType,
  ResourceType,
  Comment as CommentType,
} from "../generated/stories/v1/story_pb";

function getMonthColor(monthNumber: number): string {
  const colors = [
    "#F4A261", // Month 1 - Orange
    "#E76F51", // Month 2 - Coral
    "#2A9D8F", // Month 3 - Teal
    "#4ECDC4", // Month 4 - Teal
    "#95E1D3", // Month 5 - Mint
    "#E74C3C", // Month 6 - Red
    "#9B59B6", // Month 7 - Purple
    "#3498DB", // Month 8 - Blue
    "#1ABC9C", // Month 9 - Turquoise
    "#F39C12", // Month 10 - Orange
    "#E67E22", // Month 11 - Dark Orange
    "#16A085", // Month 12 - Green
  ];
  return colors[(monthNumber - 1) % colors.length];
}

function formatCommentDate(createdAt: CommentType.AsObject["createdAt"]): string {
  if (!createdAt) return "";
  const dateStr = timestampToDateString(createdAt);
  const timeStr = timestampToTimeString(createdAt);
  return `${dateStr} at ${timeStr}`;
}

function CommentBlock({
  comment,
  comments,
  formatDate,
}: {
  comment: CommentType.AsObject;
  comments: CommentType.AsObject[];
  formatDate: (createdAt: CommentType.AsObject["createdAt"]) => string;
}) {
  const replies = (comment.repliesList || []).length > 0
    ? comment.repliesList
    : comments.filter((c) => c.parentId === comment.id);
  return (
    <div
      style={css({
        padding: "1rem",
        backgroundColor: tokens.colors.background.secondary,
        borderRadius: "12px",
        borderLeft: `4px solid ${tokens.colors.brand.primary}`,
      })}
    >
      <div style={css({ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0.5rem", marginBottom: "0.5rem" })}>
        <Text variant="body" style={css({ fontWeight: 600, color: "#4A2E3D" })}>
          {comment.authorName}
        </Text>
        <Text variant="body" style={css({ fontSize: "0.8125rem", color: tokens.colors.neutral[600] })}>
          {formatDate(comment.createdAt)}
        </Text>
      </div>
      <Text variant="body" style={css({ whiteSpace: "pre-wrap", color: tokens.colors.neutral[800] })}>
        {comment.body}
      </Text>
      {replies.length > 0 && (
        <div style={css({ marginTop: "1rem", marginLeft: "1rem", paddingLeft: "1rem", borderLeft: `2px solid ${tokens.colors.neutral[300]}` })}>
          {replies.map((reply) => (
            <CommentBlock
              key={reply.id}
              comment={reply}
              comments={comments}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Story() {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const [story, setStory] = useState<StoryType.AsObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<CommentType.AsObject[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentForm, setCommentForm] = useState({
    authorName: "",
    authorEmail: "",
    body: "",
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const { open: openEmbedModal, Modal: EmbedModal } =
    useFullscreenIframe("Story media");

  useEffect(() => {
    if (!storyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    storiesService
      .getStory({ id: storyId })
      .then((data) => {
        if (!cancelled) setStory(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load story");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    setLoadingComments(true);
    setCommentError(null);
    storiesService
      .listComments(storyId)
      .then((res) => {
        if (!cancelled) setComments(res.commentsList || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setCommentError(err instanceof Error ? err.message : "Failed to load comments");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingComments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storyId || !commentForm.authorName.trim() || !commentForm.body.trim()) return;
    setSubmitLoading(true);
    setCommentError(null);
    try {
      await storiesService.createComment({
        storyId,
        authorName: commentForm.authorName.trim(),
        authorEmail: commentForm.authorEmail.trim() || undefined,
        body: commentForm.body.trim(),
      });
      const res = await storiesService.listComments(storyId);
      setComments(res.commentsList || []);
      setCommentForm({ authorName: "", authorEmail: "", body: "" });
      setCommentSuccess(true);
      setTimeout(() => setCommentSuccess(false), 3000);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : "Failed to post comment");
    } finally {
      setSubmitLoading(false);
    }
  };

  const topLevelComments = comments.filter((c) => !c.parentId);

  if (loading) {
    return (
      <Container>
        <div style={css({ textAlign: "center", padding: "3rem" })}>
          <Text variant="bodyLarge" style={css({ color: tokens.colors.neutral[600] })}>
            Loading story...
          </Text>
        </div>
      </Container>
    );
  }

  if (error || !story) {
    return (
      <Container>
        <Card variant="elevated" style={css({ margin: "3rem auto", maxWidth: "32rem" })}>
          <Text variant="bodyLarge" style={css({ color: tokens.colors.semantic.error, marginBottom: "1rem" })}>
            {error || "Story not found."}
          </Text>
          <Button variant="primary" onClick={() => navigate("/stories")}>
            Back to home
          </Button>
        </Card>
      </Container>
    );
  }

  const amazonResource = story.resourcesList?.find(
    (r) => r.type === ResourceType.RESOURCE_TYPE_AMAZON_BOOK
  );
  const youtubeResource = story.resourcesList?.find(
    (r) =>
      r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_FULL ||
      r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_SHORTS
  );
  const audioResources = story.resourcesList?.filter(
    (r) =>
      r.type === ResourceType.RESOURCE_TYPE_AUDIO ||
      r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_MUSIC
  );
  const ebookResource = story.resourcesList?.find(
    (r) => r.type === ResourceType.RESOURCE_TYPE_EBOOK
  );

  const monthColor = getMonthColor(story.monthNumber);

  return (
    <>
      <Container>
        <Card
          variant="elevated"
          style={css({
            margin: "2rem auto",
            maxWidth: "56rem",
            overflow: "hidden",
            backgroundColor: "#fff",
          })}
        >
          {/* Cover image */}
          <div style={css({ position: "relative", backgroundColor: tokens.colors.neutral[200] })}>
            <img
              src={story.coverImageUrl || "/placeholder.jpg"}
              alt={story.title}
              style={css({
                width: "100%",
                maxHeight: "420px",
                objectFit: "cover",
              })}
            />
            <div
              style={css({
                position: "absolute",
                bottom: "1rem",
                left: "1rem",
                backgroundColor: monthColor,
                padding: "0.5rem 1rem 0.5rem 1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                clipPath: "polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
              })}
            >
              <span style={css({ fontSize: "1rem" })}>⭐</span>
              <Text
                variant="body"
                style={css({
                  color: "white",
                  fontWeight: 600,
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                })}
              >
                Month {story.monthNumber} · {story.monthTitle}
              </Text>
            </div>
          </div>

          <div style={css({ padding: "1.5rem", "@media (min-width: 768px)": { padding: "2rem" } })}>
            <Text
              as="h1"
              variant="heading1"
              style={css({
                marginBottom: "1rem",
                color: "#4A2E3D",
              })}
            >
              {story.title}
            </Text>

            {story.description && (
              <Text
                variant="bodyLarge"
                style={css({
                  marginBottom: "1.5rem",
                  color: tokens.colors.neutral[700],
                  whiteSpace: "pre-wrap",
                })}
              >
                {story.description}
              </Text>
            )}

            {/* Core words */}
            {story.coreWordsList && story.coreWordsList.length > 0 && (
              <div style={css({ marginBottom: "1rem" })}>
                <Text
                  variant="body"
                  style={css({
                    fontWeight: 600,
                    color: tokens.colors.neutral[600],
                    marginBottom: "0.5rem",
                  })}
                >
                  Core words
                </Text>
                <div style={css({ display: "flex", flexWrap: "wrap", gap: "0.5rem" })}>
                  {story.coreWordsList.map((word, idx) => (
                    <span
                      key={idx}
                      style={css({
                        padding: "0.25rem 0.75rem",
                        borderRadius: "8px",
                        backgroundColor: tokens.colors.background.secondary,
                        fontSize: "0.875rem",
                        color: tokens.colors.neutral[800],
                      })}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {story.tagsList && story.tagsList.length > 0 && (
              <div style={css({ marginBottom: "1.5rem" })}>
                <Text
                  variant="body"
                  style={css({
                    fontWeight: 600,
                    color: tokens.colors.neutral[600],
                    marginBottom: "0.5rem",
                  })}
                >
                  Tags
                </Text>
                <div style={css({ display: "flex", flexWrap: "wrap", gap: "0.5rem" })}>
                  {story.tagsList.map((tag, idx) => (
                    <span
                      key={idx}
                      style={css({
                        padding: "0.25rem 0.5rem",
                        borderRadius: "6px",
                        backgroundColor: tokens.colors.neutral[200],
                        fontSize: "0.8125rem",
                        color: tokens.colors.neutral[600],
                      })}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Resource buttons */}
            <div style={css({ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" })}>
              <Text
                variant="body"
                style={css({
                  fontWeight: 600,
                  color: tokens.colors.neutral[600],
                  marginRight: "0.5rem",
                })}
              >
                Links
              </Text>
              {amazonResource?.available && amazonResource.url && (
                <Button
                  variant="secondary"
                  onClick={() => window.open(amazonResource.url, "_blank")}
                  style={css({ backgroundColor: "#FF8C42", color: "#4A2E3D", border: "none" })}
                >
                  🛒 Shop this book
                </Button>
              )}
              {youtubeResource?.available && youtubeResource.url && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    openEmbedModal(toEmbeddableUrl(youtubeResource.url), {
                      title: `${story.title} – Video`,
                    })
                  }
                  style={css({ backgroundColor: monthColor, color: "white", border: "none" })}
                >
                  ▶ Watch video
                </Button>
              )}
              {audioResources?.map(
                (resource, idx) =>
                  resource.available &&
                  resource.url && (
                    <Button
                      key={idx}
                      variant="secondary"
                      onClick={() =>
                        isYoutubeUrl(resource.url)
                          ? openEmbedModal(toEmbeddableUrl(resource.url), {
                              title: `${story.title} – Music`,
                            })
                          : window.open(resource.url, "_blank")
                      }
                      style={css({ backgroundColor: "#D4A5C7", color: "white", border: "none" })}
                    >
                      ♪ {isYoutubeUrl(resource.url) ? "Watch music" : "Listen"}
                    </Button>
                  )
              )}
              {ebookResource?.available && ebookResource.url && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    openEmbedModal(toEmbeddableUrl(ebookResource.url), {
                      title: `${story.title} – Ebook`,
                    })
                  }
                  style={css({ backgroundColor: "#90CDCE", color: "white", border: "none" })}
                >
                  📖 Read ebook
                </Button>
              )}
            </div>

            {/* Comments */}
            <div
              style={css({
                marginTop: "2.5rem",
                paddingTop: "2rem",
                borderTop: `1px solid ${tokens.colors.neutral[200]}`,
              })}
            >
              <Text
                as="h2"
                variant="heading2"
                style={css({
                  marginBottom: "1rem",
                  color: "#4A2E3D",
                })}
              >
                Thoughts from readers
              </Text>

              {loadingComments ? (
                <Text variant="body" style={css({ color: tokens.colors.neutral[600] })}>
                  Loading comments...
                </Text>
              ) : commentError ? (
                <Text variant="body" style={css({ color: tokens.colors.semantic.error, marginBottom: "1rem" })}>
                  {commentError}
                </Text>
              ) : topLevelComments.length === 0 ? (
                <Text variant="body" style={css({ color: tokens.colors.neutral[600], marginBottom: "1rem" })}>
                  No comments yet. Share your thoughts below!
                </Text>
              ) : (
                <div style={css({ display: "flex", flexDirection: "column", gap: "1.25rem", marginBottom: "1rem" })}>
                  {topLevelComments.map((comment) => (
                    <CommentBlock key={comment.id} comment={comment} comments={comments} formatDate={formatCommentDate} />
                  ))}
                </div>
              )}

              {/* Always show "Leave a comment" so users can post even if listing failed */}
              <div style={css({ marginTop: "2rem" })}>
                <Text
                  variant="body"
                  style={css({
                    fontWeight: 600,
                    color: tokens.colors.neutral[600],
                    marginBottom: "0.75rem",
                  })}
                >
                  Leave a comment
                </Text>
                <form onSubmit={handleSubmitComment}>
                  <div style={css({ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "32rem" })}>
                    <div>
                      <label
                        htmlFor="comment-name"
                        style={css({
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: tokens.colors.neutral[600],
                          marginBottom: "0.25rem",
                        })}
                      >
                        Name *
                      </label>
                      <input
                        id="comment-name"
                        type="text"
                        required
                        value={commentForm.authorName}
                        onChange={(e) =>
                          setCommentForm((prev) => ({ ...prev, authorName: e.target.value }))
                        }
                        placeholder="Your name"
                        style={css({
                          width: "100%",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "8px",
                          border: `1px solid ${tokens.colors.neutral[300]}`,
                          fontSize: "0.9375rem",
                        })}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="comment-email"
                        style={css({
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: tokens.colors.neutral[600],
                          marginBottom: "0.25rem",
                        })}
                      >
                        Email (optional)
                      </label>
                      <input
                        id="comment-email"
                        type="email"
                        value={commentForm.authorEmail}
                        onChange={(e) =>
                          setCommentForm((prev) => ({ ...prev, authorEmail: e.target.value }))
                        }
                        placeholder="your@email.com"
                        style={css({
                          width: "100%",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "8px",
                          border: `1px solid ${tokens.colors.neutral[300]}`,
                          fontSize: "0.9375rem",
                        })}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="comment-body"
                        style={css({
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: tokens.colors.neutral[600],
                          marginBottom: "0.25rem",
                        })}
                      >
                        Your thoughts *
                      </label>
                      <textarea
                        id="comment-body"
                        required
                        rows={4}
                        value={commentForm.body}
                        onChange={(e) =>
                          setCommentForm((prev) => ({ ...prev, body: e.target.value }))
                        }
                        placeholder="What did you think of this story?"
                        style={css({
                          width: "100%",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "8px",
                          border: `1px solid ${tokens.colors.neutral[300]}`,
                          fontSize: "0.9375rem",
                          fontFamily: "inherit",
                        })}
                      />
                    </div>
                    {commentSuccess && (
                      <Text variant="body" style={css({ color: tokens.colors.semantic.success })}>
                        Thanks! Your comment has been posted.
                      </Text>
                    )}
                    <Button type="submit" variant="primary" disabled={submitLoading}>
                      {submitLoading ? "Posting..." : "Post comment"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            <div style={css({ marginTop: "2rem" })}>
              <Button variant="secondary" onClick={() => navigate("/stories")}>
                ← Back to library
              </Button>
            </div>
          </div>
        </Card>
      </Container>
      <EmbedModal />
    </>
  );
}
